import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage, API_BASE } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import LinkedEntityFilters from "../components/LinkedEntityFilters";
import { Download, FileText, RefreshCw, ClipboardCheck, AlertOctagon, Activity, Clock, AlertTriangle, ShieldCheck, Route, Mail, Gauge } from "lucide-react";
import { toast } from "sonner";
import OperationalMetricsTab from "../components/OperationalMetricsTab";
import SitePerformanceTab from "../components/SitePerformanceTab";

const ALL_REPORTS = [
  { key: "metrics", label: "Metrics", icon: Gauge, desc: "Coverage fulfillment, unrealized hours/revenue, tardy & early-out rates, incidents, tours, and no-show report." },
  { key: "site-performance", label: "Site Performance", icon: Activity, desc: "Per-site scorecard of late/early clock-ins, missed clock-outs, incidents, MRs, geofence breaches, and tour completion." },
  { key: "attendance", label: "Attendance", icon: ClipboardCheck, desc: "Every clock-in / clock-out with the guard, site, and geo coords." },
  { key: "incidents", label: "Incident Reports", icon: AlertOctagon, desc: "Site incidents in the range — severity, status, and narrative." },
  { key: "dar", label: "Daily Activity", icon: Activity, desc: "One row per shift: scheduled vs. actual, guards, incidents at site during the window." },
  { key: "tour-completion", label: "Tour Completion", icon: Route, desc: "Patrol completion % per shift and the list of missed stops." },
  { key: "tardies-history", label: "Tardies (History)", icon: Clock, desc: "Per-guard rollup of on-time / tardy / no-show counts and total late minutes." },
  { key: "compliance", label: "Compliance", icon: ShieldCheck, desc: "Officers with deactivation dates approaching, missing certs, or low hours caps." },
];

const CLIENT_VISIBLE = new Set(["incidents", "dar", "tour-completion"]);

function isoDate(d) { return d.toISOString().slice(0, 10); }
function defaultRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { start: isoDate(start), end: isoDate(end) };
}

function ReportTable({ rows, columns }) {
  if (rows.length === 0) {
    return <div className="text-center py-16 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]">No rows in this range.</div>;
  }
  const cellValue = (r, c) =>
    c.render ? c.render(r) : (Array.isArray(r[c.key]) ? r[c.key].join(", ") : (r[c.key] ?? "—"));
  return (
    <>
      {/* Desktop / tablet — keep as a real <table> */}
      <div
        className="hidden md:block border border-[#27272A] bg-[#121212] overflow-x-auto"
        data-testid="report-table"
      >
        <table className="w-full text-xs">
          <thead className="border-b border-[#27272A]">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-3 py-2 text-left"><div className="label-mute">{c.label}</div></th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#27272A]">
            {rows.map((r, idx) => (
              <tr key={r.id || r._id || r.shift_id || r.created_at || idx} className="hover:bg-[#1c1c1e]">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-zinc-300 align-top">
                    {cellValue(r, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile — card stack. Each row becomes a tap-friendly card with
          label/value pairs so no horizontal scrolling / pinch-zoom needed. */}
      <div className="md:hidden space-y-2" data-testid="report-table-mobile">
        {rows.map((r, idx) => (
          <div
            key={r.id || r._id || r.shift_id || r.created_at || idx}
            className="border border-[#27272A] bg-[#121212] p-3"
          >
            {columns.map((c) => {
              const v = cellValue(r, c);
              return (
                <div key={c.key} className="grid grid-cols-[110px_1fr] gap-2 py-1 border-b last:border-b-0 border-[#1f1f22]">
                  <div className="label-mute self-start">{c.label}</div>
                  <div className="text-zinc-200 text-xs break-words min-w-0">{v}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

const COLUMNS = {
  attendance: [
    { key: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "action", label: "Action", render: (r) => <PillBadge tone={r.action === "check_in" ? "green" : "amber"}>{r.action}</PillBadge> },
    { key: "guard_name", label: "Officer" },
    { key: "badge_no", label: "Badge" },
    { key: "site_name", label: "Site" },
    { key: "client_name", label: "Client" },
    { key: "lat", label: "Lat" },
    { key: "lng", label: "Lng" },
  ],
  incidents: [
    { key: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "title", label: "Title" },
    { key: "severity", label: "Severity", render: (r) => <PillBadge tone={({critical:"red",high:"red",medium:"amber",low:"default"}[r.severity])||"default"}>{r.severity}</PillBadge> },
    { key: "status", label: "Status" },
    { key: "site_name", label: "Site" },
    { key: "guard_name", label: "Officer" },
    { key: "description", label: "Description", render: (r) => <span className="line-clamp-2 max-w-md inline-block">{r.description}</span> },
  ],
  dar: [
    { key: "date", label: "Date" },
    { key: "site_name", label: "Site" },
    { key: "client_name", label: "Client" },
    { key: "scheduled_start", label: "Start", render: (r) => new Date(r.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
    { key: "scheduled_end", label: "End", render: (r) => new Date(r.scheduled_end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
    { key: "assigned_guards", label: "Officers" },
    { key: "guards_count", label: "Staffed", render: (r) => `${r.guards_count}/${r.min_guards}` },
    { key: "first_check_in_at", label: "First Check-in", render: (r) => r.first_check_in_at ? new Date(r.first_check_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—" },
    { key: "last_check_out_at", label: "Last Check-out", render: (r) => r.last_check_out_at ? new Date(r.last_check_out_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—" },
    { key: "incidents_during", label: "Incidents" },
  ],
  "tardies-history": [
    { key: "guard_name", label: "Officer" },
    { key: "badge_no", label: "Badge" },
    { key: "shifts", label: "Shifts" },
    { key: "on_time", label: "On Time", render: (r) => <span className="text-[#34C759] font-mono">{r.on_time}</span> },
    { key: "tardy", label: "Tardy", render: (r) => <span className="text-[#FF9F0A] font-mono">{r.tardy}</span> },
    { key: "no_show", label: "No-show", render: (r) => <span className="text-[#FF3B30] font-mono">{r.no_show}</span> },
    { key: "total_late_minutes", label: "Total Late (min)" },
  ],
  "open-shifts": [
    { key: "date", label: "Date" },
    { key: "site_name", label: "Site" },
    { key: "client_name", label: "Client" },
    { key: "start_time", label: "Start", render: (r) => new Date(r.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
    { key: "end_time", label: "End", render: (r) => new Date(r.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
    { key: "kind", label: "Kind", render: (r) => <PillBadge tone={r.kind === "open" ? "amber" : "default"}>{r.kind}</PillBadge> },
    { key: "guards_assigned", label: "Assigned" },
    { key: "min_guards", label: "Min Required" },
  ],
  compliance: [
    { key: "guard_name", label: "Officer" },
    { key: "badge_no", label: "Badge" },
    { key: "role", label: "Role" },
    { key: "status", label: "Status" },
    { key: "flags", label: "Flags", render: (r) => <span className="text-[#FF9F0A]">{(r.flags || []).join(" · ")}</span> },
  ],
  "tour-completion": [
    { key: "shift_start", label: "Shift Start", render: (r) => new Date(r.shift_start).toLocaleString() },
    { key: "site_name", label: "Site" },
    { key: "client_name", label: "Client" },
    { key: "stops_configured", label: "Stops" },
    { key: "visits_completed", label: "Visited", render: (r) => `${r.visits_completed} / ${r.visits_expected}` },
    { key: "completion_pct", label: "Completion", render: (r) => (
        <PillBadge tone={r.completion_pct >= 100 ? "green" : r.completion_pct >= 75 ? "amber" : "red"}>
          {r.completion_pct}%
        </PillBadge>
      ) },
    { key: "missed_stops", label: "Missed", render: (r) => (
        (r.missed_stops || []).length === 0 ? <span className="text-zinc-500">—</span>
        : <span className="text-[#FF9F0A]">{(r.missed_stops || []).join(" · ")}</span>
      ) },
  ],
};

export default function Reports() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const availableReports = useMemo(
    () => ALL_REPORTS.filter((r) => !isClient || CLIENT_VISIBLE.has(r.key)),
    [isClient]
  );

  const [activeKey, setActiveKey] = useState(availableReports[0]?.key);
  const [{ start, end }, setRange] = useState(defaultRange());
  const [entity, setEntity] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    if (!activeKey) return;
    if (activeKey === "metrics" || activeKey === "site-performance") { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { start, end };
      if (entity.site_ids.length) params.site_id = entity.site_ids.join(",");
      if (entity.guard_ids.length) params.guard_id = entity.guard_ids.join(",");
      if (entity.client_names.length) params.client_name = entity.client_names.join(",");
      const res = await api.get(`/reports/${activeKey}`, { params });
      setData({ rows: res.data.rows, count: res.data.count });
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load report"));
      setData({ rows: [], count: 0 });
    }
    setLoading(false);
  }, [activeKey, start, end, entity]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportTo = (fmt) => {
    const params = new URLSearchParams({ start, end, format: fmt });
    if (entity.site_ids.length) params.set("site_id", entity.site_ids.join(","));
    if (entity.guard_ids.length) params.set("guard_id", entity.guard_ids.join(","));
    if (entity.client_names.length) params.set("client_name", entity.client_names.join(","));
    window.open(`${API_BASE}/reports/${activeKey}?${params.toString()}`, "_blank", "noopener");
  };

  const columns = COLUMNS[activeKey] || [];
  const showSiteFilter = ["attendance", "incidents", "dar", "open-shifts", "tardies-history", "tour-completion"].includes(activeKey);
  const showGuardFilter = ["attendance", "tardies-history"].includes(activeKey) && !isClient;
  const showClientFilter = ["attendance", "incidents", "dar", "open-shifts", "tardies-history", "tour-completion"].includes(activeKey) && !isClient;
  const showDateRange = activeKey !== "compliance";

  // Memoise the LinkedEntityFilters `show` prop so its identity is stable
  // across re-renders when the boolean values haven't changed.
  const linkedFiltersShow = useMemo(
    () => ({ client: showClientFilter, site: showSiteFilter, guard: showGuardFilter }),
    [showClientFilter, showSiteFilter, showGuardFilter],
  );

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle={isClient
          ? "Incident reports and Daily Activity Reports for your sites. Pick a date range and (optionally) a specific site."
          : "Operational reports for attendance, incidents, daily activity, tardies, open shifts, and compliance. Filter by date range, site, or guard. Export as CSV or PDF."}
        action={isClient ? (
          <GhostButton data-testid="back-to-portal" onClick={() => window.location.assign("/portal")}>← Back to Portal</GhostButton>
        ) : null}
      />
      <div className="p-4 md:p-8 space-y-4">
        {/* Tab bar */}
        <div className="flex flex-wrap gap-1 border-b border-[#27272A]" data-testid="reports-tabs">
          {availableReports.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`reports-tab-${key}`}
              onClick={() => { setActiveKey(key); setEntity({ client_names: [], site_ids: [], guard_ids: [] }); }}
              className={`px-4 py-2.5 text-xs uppercase tracking-widest border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                activeKey === key
                  ? "border-[#FF3B30] text-white"
                  : "border-transparent text-zinc-500 hover:text-white"
              }`}
            >
              <Icon className="size-3.5" />{label}
            </button>
          ))}
        </div>

        {/* Active report description */}
        <div className="text-xs text-zinc-500" data-testid="reports-description">
          {availableReports.find((r) => r.key === activeKey)?.desc}
        </div>

        {/* Filters */}
        {activeKey !== "metrics" && activeKey !== "site-performance" && (
        <div className="space-y-3" data-testid="reports-filters">
          {(showClientFilter || showSiteFilter || showGuardFilter) && (
            <LinkedEntityFilters
              testIdPrefix="report"
              value={entity}
              onChange={setEntity}
              show={linkedFiltersShow}
            />
          )}
          <div className="flex flex-wrap items-end gap-3">
            {showDateRange && (
              <>
                <div>
                  <div className="label-mute mb-1">Start</div>
                  <input data-testid="report-start" type="date" className={inputClass} value={start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
                </div>
                <div>
                  <div className="label-mute mb-1">End</div>
                  <input data-testid="report-end" type="date" className={inputClass} value={end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
                </div>
              </>
            )}
            <div className="ml-auto inline-flex items-center gap-3">
              <div className="text-xs font-mono text-zinc-500 pb-2" data-testid="report-count">{loading ? "…" : `${data.count} row${data.count === 1 ? "" : "s"}`}</div>
              <GhostButton data-testid="report-refresh" onClick={fetchReport}><RefreshCw className="size-3.5" /> Refresh</GhostButton>
              <GhostButton data-testid="report-export-csv" onClick={() => exportTo("csv")}><Download className="size-3.5" /> CSV</GhostButton>
              <GhostButton data-testid="report-export-pdf" onClick={() => exportTo("pdf")}><FileText className="size-3.5" /> PDF</GhostButton>
            </div>
          </div>
        </div>
        )}

        {activeKey === "compliance" && <CertificationsPanel />}

        {activeKey === "metrics" ? (
          <OperationalMetricsTab />
        ) : activeKey === "site-performance" ? (
          <SitePerformanceTab />
        ) : loading ? (
          <div className="text-center py-16 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]">Loading report…</div>
        ) : (
          <ReportTable rows={data.rows} columns={columns} />
        )}
      </div>
    </div>
  );
}

function CompliancePanelSection({ title, rows, tone, testid }) {
  return (
    <div className="border border-[#27272A] bg-[#121212]" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
        <div>
          <div className="label-mute">{title}</div>
          <div className="font-display text-2xl font-black tracking-tight">{rows.length}</div>
        </div>
        <PillBadge tone={tone}>{tone === "red" ? "Action needed" : tone === "amber" ? "Plan ahead" : "OK"}</PillBadge>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-zinc-500 scanlines">None.</div>
      ) : (
        <div className="divide-y divide-[#27272A] max-h-72 overflow-auto">
          {rows.slice(0, 100).map((r, i) => (
            <div key={`${r.guard_id || r.guard_name || ""}-${r.name || ""}-${i}`} className="px-4 py-2.5 flex items-center justify-between text-xs">
              <div className="min-w-0">
                <div className="font-medium truncate">{r.guard_name}</div>
                <div className="text-zinc-500 font-mono text-[10px]">#{r.badge_no || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono">{r.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {r.no_expiry ? "No expiry" : (
                    r.days_until_expiry == null ? "—" :
                    r.days_until_expiry < 0 ? `${Math.abs(r.days_until_expiry)} days ago` :
                    `${r.days_until_expiry} day${r.days_until_expiry === 1 ? "" : "s"}`
                  )}
                  {r.expires_at && <span className="ml-2 text-zinc-600">{r.expires_at}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CertificationsPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    api.get("/guards/compliance/certifications")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const runCertCheck = async () => {
    setNotifying(true);
    try {
      const r = await api.post("/notifications/cert-check");
      const rows = r.data?.results || [];
      const total = rows.reduce((n, x) => n + (x.sent || 0), 0);
      const skipped = rows.reduce((n, x) => n + (x.skipped_already_sent || 0), 0);
      const pending = rows.reduce((n, x) => n + (x.pending_unconfigured || 0), 0);
      if (total > 0) {
        toast.success(`Sent ${total} cert-expiry email${total === 1 ? "" : "s"}${skipped ? ` · ${skipped} already alerted` : ""}`);
      } else if (pending > 0) {
        toast.warning(`${pending} cert${pending === 1 ? "" : "s"} waiting — set RESEND_API_KEY on the backend to enable email sending`);
      } else if (skipped > 0) {
        toast.info(`No new alerts · ${skipped} cert${skipped === 1 ? "" : "s"} already notified`);
      } else {
        toast.info("No certifications currently within 30 / 7 / 0 day windows");
      }
    } catch (e) {
      toast.error("Cert-check failed. Is RESEND_API_KEY set on the backend?");
    } finally {
      setNotifying(false);
    }
  };

  const runDarCheck = async () => {
    setNotifying(true);
    try {
      const r = await api.post("/notifications/dar-missing-check");
      const rows = r.data?.results || [];
      const total = rows.reduce((n, x) => n + (x.sent || 0), 0);
      const skipped = rows.reduce((n, x) => n + (x.skipped_already_sent || 0), 0);
      const pending = rows.reduce((n, x) => n + (x.pending_unconfigured || 0), 0);
      if (total > 0) {
        toast.success(`Sent ${total} DAR-missing reminder${total === 1 ? "" : "s"}${skipped ? ` · ${skipped} already reminded` : ""}`);
      } else if (pending > 0) {
        toast.warning(`${pending} DAR-missing reminder${pending === 1 ? "" : "s"} waiting — set RESEND_API_KEY to send`);
      } else if (skipped > 0) {
        toast.info(`No new reminders · ${skipped} already sent`);
      } else {
        toast.info("No shifts missing DAR in the 2h / 24h windows");
      }
    } catch (e) {
      toast.error("DAR-missing check failed.");
    } finally {
      setNotifying(false);
    }
  };

  if (loading) {
    return <div className="border border-[#27272A] bg-[#121212] p-4 text-xs text-zinc-500">Loading certifications…</div>;
  }
  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="certifications-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">
          Certification window: <span className="text-zinc-300 font-mono">{data.threshold_days} days</span> · as of <span className="text-zinc-300 font-mono">{data.as_of}</span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <GhostButton
              data-testid="run-cert-notify"
              onClick={runCertCheck}
              disabled={notifying}
              title="Scan certifications and email admins + affected  for any that crossed a 30/7/0-day expiry threshold"
            >
              <Mail className="size-3.5" /> {notifying ? "Scanning…" : "Notify certs"}
            </GhostButton>
            <GhostButton
              data-testid="run-dar-notify"
              onClick={runDarCheck}
              disabled={notifying}
              title="Scan shifts that ended 2h+ or 24h+ ago without a DAR and email the  + supervisors"
            >
              <Mail className="size-3.5" /> {notifying ? "Scanning…" : "DAR reminders"}
            </GhostButton>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CompliancePanelSection title="Expired" rows={data.expired} tone="red" testid="certs-expired-section" />
        <CompliancePanelSection title="Nearing Expiry" rows={data.nearing_expiry} tone="amber" testid="certs-nearing-section" />
        <CompliancePanelSection title="No Expiry" rows={data.no_expiry} tone="default" testid="certs-no-expiry-section" />
      </div>
    </div>
  );
}
